import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { z } from 'zod';
import { AlertsMatrixPage, type MatrixSearch } from '../../../features/B4/AlertsMatrix';
import { SummariesMatrix } from '../../../features/B6/SummariesMatrix';

const ID = /^[\w-]{1,64}$/;

const searchSchema = z.object({
  tab: z.enum(['alerts', 'summaries']).optional().catch(undefined),
  segment: z.string().trim().max(64).optional().catch(undefined),
  parent: z.string().trim().max(64).optional().catch(undefined),
  status: z.enum(['active', 'draft', 'paused', 'all']).optional().catch(undefined),
  only: z.enum(['overrides', 'on']).optional().catch(undefined),
  ids: z
    .string()
    .refine((s) => {
      const ids = s.split(',');
      return ids.length <= 200 && ids.every((id) => ID.test(id));
    })
    .optional()
    .catch(undefined),
  q: z.string().max(120).optional().catch(undefined),
});

/** #/clients/matrix[?tab=&segment=&parent=&status=&only=&ids=&q=] — the clients × alerts matrix (W2-2-6) and its summaries tab (W2-2-7). */
export const Route = createFileRoute('/_app/clients/matrix')({
  validateSearch: searchSchema,
  component: MatrixRoute,
});

function MatrixRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/clients/matrix' });
  const onSearch = useCallback((s: MatrixSearch) => void navigate({ search: s, replace: true }), [navigate]);
  if (search.tab === 'summaries') return <SummariesMatrix search={search} onSearch={onSearch} />;
  return <AlertsMatrixPage search={search} onSearch={onSearch} />;
}
